import { useEffect, useMemo, useState } from "react";
import { api, formatMs, type RoutingDecisionResponse } from "../lib/api";
import {
  Dot,
  ErrorState,
  PageHeader,
  ProviderMark,
  SectionHeader,
  StatusBadge,
  TraceStep,
  toneForStatus,
} from "../components/ui";

interface RouterState {
  profiles: Array<{ id: string; displayName: string; description: string; preferFree: boolean }>;
  candidates: Array<{
    providerId: string;
    providerTypeId: string;
    providerDisplayName: string;
    modelId: string;
    logicalId: string;
    displayName: string;
    pricing: { tier: string };
    health: { state: string };
    quota: { remainingRatio: number | null; source: string };
    providerStatus: string;
  }>;
  circuits: Array<{ key: string; state: string; failures: number; label?: string }>;
}

const INTENTS = [
  { id: "auto", label: "Auto" },
  { id: "free-auto", label: "Free" },
  { id: "coding-auto", label: "Coding" },
  { id: "vision-auto", label: "Vision" },
  { id: "embedding-auto", label: "Embeddings" },
  { id: "fast-auto", label: "Fast" },
  { id: "budget-auto", label: "Budget" },
];

export default function RouterPage() {
  const [state, setState] = useState<RouterState | null>(null);
  const [model, setModel] = useState("free-auto");
  const [prompt, setPrompt] = useState("Write a TypeScript function that debounces input.");
  const [profile, setProfile] = useState("");
  const [requireFree, setRequireFree] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(false);
  const [decision, setDecision] = useState<RoutingDecisionResponse | null>(null);
  const [poolQuery, setPoolQuery] = useState("");
  const [poolShown, setPoolShown] = useState(40);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    void api<RouterState>(`/api/router${includeDemo ? "?demo=1" : ""}`)
      .then(setState)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Failed to load router state"),
      );
  }, [includeDemo]);

  async function simulate() {
    setRunning(true);
    setError(null);
    try {
      const d = await api<RoutingDecisionResponse>("/api/router/simulate", {
        method: "POST",
        body: JSON.stringify({
          model,
          messages: [{ role: "user", content: prompt }],
          require_free: requireFree,
          routing_profile: profile || undefined,
          include_demo: includeDemo,
        }),
      });
      setDecision(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Simulate failed");
    } finally {
      setRunning(false);
    }
  }

  const ex = decision?.explanation;
  const isAuto = INTENTS.some((i) => i.id === model);
  const filteredPool = useMemo(() => {
    if (!state) return [];
    const q = poolQuery.trim().toLowerCase();
    if (!q) return state.candidates;
    return state.candidates.filter((c) =>
      `${c.providerDisplayName} ${c.displayName} ${c.providerTypeId}`
        .toLowerCase()
        .includes(q),
    );
  }, [state, poolQuery]);

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <PageHeader
        title="Routing Lab"
        subtitle="See exactly how NEXUS turns a request into a route."
      />

      <div className="grid gap-4 xl:grid-cols-5">
        {/* ── Composer ───────────────────────────────────────── */}
        <section className="panel h-fit p-4 xl:col-span-2">
          <h2 className="mb-3 text-sm font-semibold">Request</h2>

          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
            Intent
          </label>
          <div className="mb-3 flex flex-wrap gap-1">
            {INTENTS.map((i) => (
              <button
                key={i.id}
                onClick={() => setModel(i.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  model === i.id
                    ? "bg-accent/15 text-accent"
                    : "text-canvas-muted hover:bg-canvas-raised"
                }`}
              >
                {i.label}
              </button>
            ))}
          </div>
          {!isAuto && null}
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="mb-3 w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
            aria-label="Model"
          >
            {!INTENTS.some((i) => i.id === model) && <option value={model}>{model}</option>}
            {state?.candidates.map((c) => (
              <option key={c.modelId} value={`${c.providerTypeId}/${c.logicalId}`}>
                {c.providerDisplayName} / {c.displayName}
              </option>
            ))}
          </select>

          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
            Prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            className="mb-3 w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
          />

          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
            Constraints
          </label>
          <div className="mb-3 space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={requireFree}
                onChange={(e) => setRequireFree(e.target.checked)}
                className="accent-[#3DDC97]"
              />
              Free only
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeDemo}
                onChange={(e) => setIncludeDemo(e.target.checked)}
                className="accent-[#3DDC97]"
              />
              Include demo providers
              <span className="text-[11px] text-canvas-muted">(testing only — off in production)</span>
            </label>
            <select
              value={profile}
              onChange={(e) => setProfile(e.target.value)}
              className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
            >
              <option value="">Profile: intent default</option>
              {state?.profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>

          <button
            className="btn-primary w-full justify-center"
            onClick={() => void simulate()}
            disabled={running}
          >
            {running ? "Routing…" : "Run routing simulation"}
          </button>
        </section>

        {/* ── Decision pipeline ──────────────────────────────── */}
        <section className="space-y-4 xl:col-span-3">
          {error && <ErrorState message={error} />}

          {!decision && !error && (
            <div className="panel flex flex-col items-center justify-center px-6 py-12 text-center">
              <p className="mb-1 text-sm font-medium">Run a simulation</p>
              <p className="max-w-md text-sm text-canvas-muted">
                Pick an intent, add a prompt, and NEXUS will show the full decision
                pipeline — which models qualified, why they won, and what the
                fallback chain looks like.
              </p>
            </div>
          )}

          {decision && ex && (
            <div className="panel p-4">
              <h2 className="mb-3 text-sm font-semibold">Decision pipeline</h2>
              <ol>
                <TraceStep
                  index={1}
                  title="Request received"
                  detail={
                    <>
                      intent <span className="mono">{ex.intent}</span> · task{" "}
                      <span className="mono">{ex.taskCategory}</span> · profile{" "}
                      <span className="mono">{ex.profile}</span>
                      {requireFree && <span className="chip-ok ml-1 !text-[10px]">free only</span>}
                    </>
                  }
                  tone="ok"
                />
                <TraceStep
                  index={2}
                  title="Capability analysis"
                  detail={
                    ex.requiredCapabilities.length
                      ? `requires: ${ex.requiredCapabilities.join(", ")}`
                      : "no hard capability requirements"
                  }
                  tone="ok"
                />
                <TraceStep
                  index={3}
                  title="Candidate discovery"
                  detail={`${ex.accepted.length + ex.rejected.length} candidates evaluated`}
                  tone="ok"
                />
                <TraceStep
                  index={4}
                  title="Filtering"
                  detail={
                    <>
                      <span className="text-accent">{ex.accepted.length} accepted</span>
                      {" · "}
                      <span className={ex.rejected.length ? "text-danger" : "text-canvas-muted"}>
                        {ex.rejected.length} rejected
                      </span>
                    </>
                  }
                  tone={ex.accepted.length ? "ok" : "danger"}
                />
                <TraceStep
                  index={5}
                  title="Selected route"
                  detail={
                    ex.selected
                      ? `${ex.selected.providerDisplayName} / ${ex.selected.displayName}`
                      : "No eligible candidate"
                  }
                  tone={ex.selected ? "ok" : "danger"}
                />
                <TraceStep
                  index={6}
                  title="Fallback chain"
                  last
                  detail={
                    ex.fallbackChain.length
                      ? `${ex.fallbackChain.length} alternate${ex.fallbackChain.length === 1 ? "" : "s"} staged`
                      : "none"
                  }
                  tone={ex.fallbackChain.length ? "ok" : "muted"}
                />
              </ol>
            </div>
          )}

          {decision && ex?.selected && (
            <div className="panel p-4">
              <SectionHeader title="Why this route" />
              <div className="mb-2 flex items-center gap-2">
                <span className="text-base font-semibold text-accent">
                  {ex.selected.providerDisplayName} / {ex.selected.displayName}
                </span>
              </div>
              <ul className="mb-3 space-y-1 text-sm">
                {ex.selected.reasons.map((r) => (
                  <li key={r} className="flex gap-2">
                    <span className="text-accent">✓</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-3">
                {Object.entries(ex.selected.scoreBreakdown)
                  .filter(([k]) => k !== "total")
                  .map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-canvas px-2 py-1.5">
                      <div className="text-canvas-muted">{k}</div>
                      <div className="mono">{Number(v).toFixed(3)}</div>
                    </div>
                  ))}
                {ex.selected.scoreBreakdown.total != null && (
                  <div className="rounded-lg bg-accent/10 px-2 py-1.5">
                    <div className="text-accent">total</div>
                    <div className="mono font-medium text-accent">
                      {ex.selected.scoreBreakdown.total.toFixed(3)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {decision && ex && ex.fallbackChain.length > 0 && (
            <div className="panel">
              <div className="panel-header">
                <h2 className="text-sm font-semibold">Fallback chain</h2>
              </div>
              <ol className="divide-y divide-canvas-border">
                {ex.fallbackChain.map((c, i) => (
                  <li
                    key={`${c.modelId}-${i}`}
                    className="flex items-center justify-between px-4 py-2 text-sm"
                  >
                    <span>
                      <span className="mono mr-2 text-canvas-muted">#{i + 1}</span>
                      {c.displayName}
                    </span>
                    <span className={c.role === "primary" ? "chip-ok" : "chip-muted"}>
                      {c.role}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {decision && ex && (ex.accepted.length > 0 || ex.rejected.length > 0) && (
            <details className="panel group">
              <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold">
                <span>
                  Candidate analysis
                  <span className="ml-2 font-normal text-canvas-muted">
                    {ex.accepted.length} accepted · {ex.rejected.length} rejected
                  </span>
                </span>
                <span className="text-xs text-canvas-muted transition group-open:rotate-180">▾</span>
              </summary>
              <div className="grid gap-4 border-t border-canvas-border p-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-xs font-semibold">Accepted</h3>
                    <span className="chip-ok">{ex.accepted.length}</span>
                  </div>
                  <ul className="max-h-56 divide-y divide-canvas-border overflow-y-auto text-sm">
                    {ex.accepted.map((c, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 py-1.5">
                        <span className="min-w-0 truncate">
                          {c.providerDisplayName} / {c.displayName}
                          {c.pricing.tier === "free" && (
                            <span className="chip-ok ml-1.5 !text-[10px]">FREE</span>
                          )}
                        </span>
                        <span className="mono shrink-0 text-canvas-muted">
                          {c.scoreBreakdown?.total.toFixed(3) ?? "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-xs font-semibold">Rejected</h3>
                    <span className={ex.rejected.length ? "chip-danger" : "chip-muted"}>
                      {ex.rejected.length}
                    </span>
                  </div>
                  <ul className="max-h-56 divide-y divide-canvas-border overflow-y-auto text-sm">
                    {ex.rejected.length === 0 && (
                      <li className="py-1.5 text-canvas-muted">None — all candidates qualified.</li>
                    )}
                    {ex.rejected.map((c, i) => (
                      <li key={i} className="py-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate">
                            {c.providerDisplayName} / {c.displayName}
                          </span>
                          <span className="chip-danger shrink-0 !text-[10px]">
                            {c.rejectionReason?.replace(/_/g, " ")}
                          </span>
                        </div>
                        {c.rejectionDetail && (
                          <div className="mt-0.5 text-[11px] text-canvas-muted">
                            {c.rejectionDetail}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </details>
          )}

          {state && (
            <details className="panel group">
              <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold">
                <span>
                  Candidate pool
                  <span className="ml-2 font-normal text-canvas-muted">
                    {state.candidates.length} deployments
                    {includeDemo && " · incl. demo"}
                  </span>
                </span>
                <span className="text-xs text-canvas-muted transition group-open:rotate-180">▾</span>
              </summary>
              <div className="border-t border-canvas-border p-4">
                <input
                  value={poolQuery}
                  onChange={(e) => {
                    setPoolQuery(e.target.value);
                    setPoolShown(40);
                  }}
                  placeholder="Filter pool — provider or model…"
                  className="mb-3 w-full rounded-lg border border-canvas-border bg-canvas px-3 py-1.5 text-sm outline-none focus:border-accent"
                />
                <div className="max-h-72 divide-y divide-canvas-border overflow-y-auto text-sm">
                  {filteredPool.slice(0, poolShown).map((c) => (
                    <div
                      key={c.modelId}
                      className="flex items-center justify-between py-1.5"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <ProviderMark
                          letter={c.providerDisplayName.slice(0, 2)}
                          accent="#3DDC97"
                          size={24}
                        />
                        <div className="min-w-0">
                          <div className="truncate">
                            {c.providerDisplayName} / {c.displayName}
                            {c.providerTypeId.startsWith("mock-") && (
                              <span className="chip-muted ml-1.5 !text-[10px]">DEMO</span>
                            )}
                          </div>
                          <div className="text-[11px] text-canvas-muted">
                            {c.pricing.tier} · quota{" "}
                            {c.quota.remainingRatio == null
                              ? "managed"
                              : `${Math.round(c.quota.remainingRatio * 100)}%`}
                          </div>
                        </div>
                      </div>
                      <StatusBadge label={c.health.state} tone={toneForStatus(c.health.state)} />
                    </div>
                  ))}
                </div>
                {filteredPool.length > poolShown && (
                  <button
                    className="btn-ghost mt-2 w-full justify-center text-xs"
                    onClick={() => setPoolShown((s) => s + 40)}
                  >
                    Show more — {filteredPool.length - poolShown} remaining
                  </button>
                )}
                {state.circuits.length > 0 && (
                  <div className="border-t border-canvas-border pt-2.5 text-[11px] text-canvas-muted">
                    Circuits:{" "}
                    {[...new Map(
                      state.circuits.map((c) => {
                        const name = c.label ?? "Unavailable provider";
                        return [
                          name,
                          `${name} ${c.state}${c.failures ? ` (${c.failures}f)` : ""}`,
                        ];
                      }),
                    ).values()].join(" · ")}
                  </div>
                )}
              </div>
            </details>
          )}
        </section>
      </div>
    </div>
  );
}
